import { useId, type ComponentPropsWithRef } from 'react';
import { Field, fieldDescribedBy } from '@src/components/Field/Field';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography, typeClassName } from '@src/components/Typography/Typography';
import './Textarea.css';

type NativeTextareaProps = Omit<ComponentPropsWithRef<'textarea'>, 'style' | 'value' | 'onChange'>;

export interface TextareaProps extends NativeTextareaProps {
  /** Controlled, always a string — an empty field is `''`, never undefined. */
  value: string;
  /** Value-first, like Input: callers want the text, not the event. */
  onValueChange: (value: string) => void;
  label?: string;
  /** Sits opposite the label, and turns the field red. */
  error?: string;
  /** Quiet note under the field. Shows alongside an error, not instead of it. */
  hint?: string;
  /**
   * Grows with its content instead of scrolling, so nothing is typed out of
   * sight. `rows` becomes the minimum height, and growth stops at
   * `--textarea-max-height`, after which the field scrolls.
   *
   * Turn it off for a field the user should be able to drag taller themselves.
   */
  autosize?: boolean;
  /**
   * Shows `used / maxLength` opposite the hint, and marks it when the limit is
   * reached — otherwise typing simply stops with no explanation. Needs
   * `maxLength` to show anything.
   */
  showCount?: boolean;
  /**
   * Keeps the label and hint rows occupied even when empty, so fields sitting
   * side by side line up whether or not each one has a label.
   */
  reserveSpace?: boolean;
  /** Parent id. The control and character count hang off it. */
  testId?: string;
}

export function Textarea({
  value,
  onValueChange,
  label,
  error,
  hint,
  autosize = true,
  showCount = false,
  reserveSpace = false,
  className,
  disabled,
  id,
  rows = 3,
  maxLength,
  testId,
  ...rest
}: TextareaProps) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;

  const limited = showCount && maxLength !== undefined;
  const atLimit = maxLength !== undefined && value.length >= maxLength;

  const countClasses = ['textarea__count'];
  if (atLimit) {
    countClasses.push('textarea__count--full');
  }

  return (
    <Field
      controlId={textareaId}
      label={label}
      error={error}
      hint={hint}
      hintEnd={
        limited ? (
          <Typography
            as="span"
            role={atLimit ? 'meta/error' : 'meta/helper'}
            className={countClasses.join(' ')}
            {...testIdAttr(testIdFor(testId, 'count'))}
          >
            {value.length} / {maxLength}
          </Typography>
        ) : undefined
      }
      disabled={disabled}
      reserveSpace={reserveSpace}
      multiline
      testId={testId}
      className={className}
    >
      {/*
        The sizer is what makes autosize work: it holds an invisible copy of the
        value in the same grid cell as the control, so the cell is always exactly
        as tall as the text. See Textarea.css.
      */}
      <div
        className={`textarea__sizer textarea__sizer--${autosize ? 'autosize' : 'manual'} ${typeClassName('label/input')}`}
        data-replicated-value={autosize ? value : undefined}
      >
        <textarea
          className={`textarea__control ${typeClassName('label/input')}`}
          id={textareaId}
          {...testIdAttr(testIdFor(testId, 'control'))}
          value={value}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={fieldDescribedBy(textareaId, { error, hint })}
          onChange={(event) => onValueChange(event.target.value)}
          {...rest}
        />
      </div>
    </Field>
  );
}
